/**
 * Read-only guard for "view as user" sessions.
 *
 * While a Super Admin is viewing the app as somebody else, the session is
 * genuinely that person's: any write it makes is recorded as theirs, by them,
 * with no marker in the row to say otherwise. Diagnosis almost never needs to
 * write, so writes are off by default and the Super Admin must turn them on
 * explicitly (which is recorded in es_impersonation_sessions.actions_enabled).
 *
 * The guard sits on the Supabase client's `global.fetch`, because that is the
 * single choke point every PostgREST query, RPC, storage call and Edge Function
 * invocation passes through. Putting it anywhere else — a service wrapper, a
 * React hook — would leave whichever call site forgot to use it unguarded.
 *
 * This is a guard against accidents, not a security boundary. The person
 * holding the tab is a Super Admin with devtools open; the point is that
 * nothing happens *by surprise*. The server-side ledger is the actual record.
 *
 * A blocked call is also announced on `window` so the UI can show what the page
 * tried to do — which is frequently the diagnosis.
 */

import {
  VIEW_AS_BLOCKED_EVENT,
  type ViewAsBlockedDetail,
} from "./viewAs";

/**
 * RPCs that only read.
 *
 * Of the RPCs the client POSTs, `set_role_signup_approval` mutates. (It also
 * POSTed `record_stage_action_and_unlock_dependencies` and
 * `finalize_untriggered_dependent_assignments` until 20261002120000 made them
 * service-role only.) An allowlist is therefore both short and safe, where
 * "allow every RPC" would quietly permit any mutating one. Anything added here
 * must be checked for writes first.
 *
 * A new read-only RPC is better called with `{ get: true }` than added here.
 * A GET passes before this list is consulted, and PostgREST always runs one in
 * a READ ONLY transaction, so the database refuses a write rather than this
 * list vouching that there is none. `bootstrap_staff_session` is called that
 * way.
 */
const READ_ONLY_RPCS = new Set<string>([
  "user_permission_keys",
]);

/**
 * Edge Functions that only read, and that the UI needs in order to render.
 *
 * `ai-translate` is the one deliberate exception: it upserts into
 * `es_translations_cache`, keyed by a hash of the source text. That is a shared
 * content cache, not user data, and it is not attributable to the target.
 * Without it every translated string renders raw, which looks like a bug and
 * would derail the diagnosis it was meant to support.
 *
 * Everything absent from this list is blocked, including `es_admin_operations`,
 * `staff-action`, `process-workflow` and `service-management`.
 */
const READ_ONLY_FUNCTIONS = new Set<string>([
  // The control plane for this feature: bind, set_actions and end must always
  // get through, or the session could not be ended from inside the tab.
  "view-as",
  "ai-translate",
  "ai-reports-query",
  "decision-target-reports",
  "public-home-stats",
  "sf-cape-directory",
  "track-application",
  "sf-track-application",
]);

const SAFE_METHODS = new Set(["GET", "HEAD", "OPTIONS"]);

export interface GuardDecision {
  allowed: boolean;
  /** Short human label for the UI, e.g. "PATCH es_applications". */
  label: string;
  /** Why it was refused; undefined when allowed. */
  reason?: string;
}

function pathnameOf(url: string): string {
  try {
    return new URL(url, "http://localhost").pathname;
  } catch {
    return url;
  }
}

/**
 * Decide whether one request may proceed. Pure, so it can be unit-tested
 * without a browser or a Supabase client.
 */
export function evaluateViewAsRequest(
  method: string,
  url: string,
): GuardDecision {
  const verb = (method || "GET").toUpperCase();
  const path = pathnameOf(url);

  if (SAFE_METHODS.has(verb)) {
    return { allowed: true, label: `${verb} ${path}` };
  }

  // Auth: token refresh, verifyOtp on the way in, signOut on the way out.
  // Blocking these would strand the session it is meant to protect.
  if (path.startsWith("/auth/v1/")) {
    return { allowed: true, label: `${verb} ${path}` };
  }

  // Creating a signed URL is a POST, but it only grants read access to a file
  // the caller can already see. Document previews depend on it.
  if (path.startsWith("/storage/v1/object/sign")) {
    return { allowed: true, label: `${verb} signed-url` };
  }

  if (path.startsWith("/rest/v1/rpc/")) {
    const name = path.slice("/rest/v1/rpc/".length).split("/")[0];
    return READ_ONLY_RPCS.has(name)
      ? { allowed: true, label: `rpc ${name}` }
      : {
        allowed: false,
        label: `rpc ${name}`,
        reason: `The database function "${name}" can change data.`,
      };
  }

  if (path.startsWith("/functions/v1/")) {
    const name = path.slice("/functions/v1/".length).split("/")[0];
    return READ_ONLY_FUNCTIONS.has(name)
      ? { allowed: true, label: `fn ${name}` }
      : {
        allowed: false,
        label: `fn ${name}`,
        reason: `The "${name}" function can change data.`,
      };
  }

  if (path.startsWith("/rest/v1/")) {
    const table = path.slice("/rest/v1/".length).split("?")[0].split("/")[0];
    return {
      allowed: false,
      label: `${verb} ${table}`,
      reason: `This would write to "${table}" as the user you are viewing.`,
    };
  }

  return {
    allowed: false,
    label: `${verb} ${path}`,
    reason: "Only reads are allowed while viewing as another user.",
  };
}

function announceBlocked(detail: ViewAsBlockedDetail): void {
  if (typeof window === "undefined") return;
  try {
    window.dispatchEvent(
      new CustomEvent<ViewAsBlockedDetail>(VIEW_AS_BLOCKED_EVENT, { detail }),
    );
  } catch {
    /* CustomEvent is unavailable in some test environments */
  }
}

/**
 * A fetch that refuses writes, shaped so supabase-js surfaces the refusal as an
 * ordinary `{ data: null, error }` rather than throwing. The body mirrors a
 * PostgREST error so existing error handling renders it unchanged.
 */
export function createViewAsFetch(
  areActionsEnabled: () => boolean,
  baseFetch: typeof fetch = fetch,
): typeof fetch {
  return async function viewAsFetch(
    input: RequestInfo | URL,
    init?: RequestInit,
  ): Promise<Response> {
    // Once the Super Admin has unblocked writes, get out of the way entirely.
    if (areActionsEnabled()) {
      return baseFetch(input, init);
    }

    const url = typeof input === "string"
      ? input
      : input instanceof URL
      ? input.toString()
      : input.url;
    const method = init?.method ??
      (typeof input === "object" && "method" in input ? input.method : "GET");

    const decision = evaluateViewAsRequest(method ?? "GET", url);
    if (decision.allowed) {
      return baseFetch(input, init);
    }

    // Loud in the console on purpose: what the page *tried* to do is often the
    // fault being investigated.
    console.warn(
      `[view-as] blocked ${decision.label} — ${decision.reason}`,
      url,
    );
    announceBlocked({
      method: (method ?? "GET").toUpperCase(),
      url,
      label: decision.label,
    });

    return new Response(
      JSON.stringify({
        code: "VIEW_AS_READ_ONLY",
        message: `Blocked in view-as mode: ${decision.label}`,
        details: decision.reason,
        hint:
          "Use “Enable actions” in the view-as banner if this write is genuinely needed.",
      }),
      {
        status: 403,
        headers: { "Content-Type": "application/json" },
      },
    );
  } as typeof fetch;
}
